import React from "react";
import {
  IonCard,
  IonCheckbox,
  IonItem,
  IonLabel,
  IonList,
  IonRadio,
  IonRadioGroup,
  IonText,
} from "@ionic/react";
import { Link } from "react-router-dom";
import { IPaymentConfig, PaymentMethod as TPaymentMethod } from "../../models";
import { PaymentBrands } from "../../components/PaymentBrands/PaymentBrands";
import InfoMessage from "../../components/InfoMessage/InfoMessage";
import { NumberMoney } from "../../components/NumberMoney/NumberMoney";
import { useSelector } from "react-redux";
import { AppState } from "../main.reducer";
import { Currency } from "../../common/constants";
import { SELLER_CARD, SELLER_COD } from "../../common/sellers";
import { orderFieldId } from "./orderValidation";

interface Props {
  config: IPaymentConfig;
  /** null until the customer chooses: the bank forbids a preselected method. */
  method: TPaymentMethod | null;
  onMethodChange: (method: TPaymentMethod) => void;
  /** Why card payment can't be used for this cart, or null. */
  cardBlockedReason: string | null;
  termsAccepted: boolean;
  onTermsChange: (accepted: boolean) => void;
  /** Amount charged, always in RSD. */
  totalDin: number;
  hasReservations: boolean;
  /** Validation messages, shown once the customer tried to order (orderValidation). */
  methodError?: string;
  termsError?: string;
}

/* IonRadio keeps its label on one line and cuts it with "…" (white-space: nowrap inside its shadow
   DOM, not exposed as a part in Ionic 7.4). Content that sets its own white-space wraps normally,
   so the description and the seller line show in full on small screens. */
const RadioLabel: React.FC = ({ children }) => (
  <span style={{ display: "block", whiteSpace: "normal" }}>{children}</span>
);

const ErrorText: React.FC = ({ children }) => (
  <IonText color="danger" style={{ display: "block", fontSize: 13, padding: "0 16px 6px" }}>
    {children}
  </IonText>
);

/**
 * "Način plaćanja": cash on delivery or card (Banca Intesa), and the consent to the purchase terms.
 * Hidden entirely while card payment is off, so checkout looks exactly as before.
 */
export const PaymentMethod: React.FC<Props> = ({
  config,
  method,
  onMethodChange,
  cardBlockedReason,
  termsAccepted,
  onTermsChange,
  totalDin,
  hasReservations,
  methodError,
  termsError,
}) => {
  const currency = useSelector<AppState, Currency>((s) => s.common.currency);
  if (config.mode === "off") return null;

  const cardDisabled = config.mode !== "on" || !!cardBlockedReason;

  return (
    <IonCard>
      <IonList>
        <IonItem lines="none" id={orderFieldId("paymentMethod")}>
          <IonLabel
            color="medium"
            className="required"
            style={methodError ? { color: "var(--ion-color-danger)" } : undefined}
          >
            Način plaćanja
          </IonLabel>
        </IonItem>
        {methodError && <ErrorText>{methodError}</ErrorText>}
        <IonRadioGroup
          value={method ?? undefined}
          onIonChange={(e) => onMethodChange(e.detail.value as TPaymentMethod)}
        >
          <IonItem>
            <IonRadio value="COD" justify="start" labelPlacement="end">
              <RadioLabel>
                Plaćanje pouzećem
                <br />
                <IonText color="medium" style={{ fontSize: 13 }}>
                  Plaćate kuriru prilikom preuzimanja paketa.
                  <br />
                  Prodavac: {SELLER_COD.name}, {SELLER_COD.address}
                </IonText>
              </RadioLabel>
            </IonRadio>
          </IonItem>
          <IonItem lines="none">
            <IonRadio
              value="CARD"
              justify="start"
              labelPlacement="end"
              disabled={cardDisabled}
            >
              <RadioLabel>
                Plati karticom
                {config.mode === "soon" && (
                  <IonText color="medium"> (uskoro)</IonText>
                )}
                <br />
                <IonText color="medium" style={{ fontSize: 13 }}>
                  {config.mode === "soon"
                    ? "Plaćanje platnim karticama uskoro će biti dostupno."
                    : cardBlockedReason ??
                      "Bezbedno plaćanje na zaštićenoj stranici banke."}
                  <br />
                  Prodavac: {SELLER_CARD.name}, {SELLER_CARD.address}
                </IonText>
              </RadioLabel>
            </IonRadio>
          </IonItem>
        </IonRadioGroup>

        {/* Consent to the purchase terms: required for every order (EPM 2.5 for cards). */}
        <div
          id={orderFieldId("terms")}
          style={{ padding: "8px 16px 4px", display: "flex", alignItems: "flex-start", gap: 10 }}
        >
          <IonCheckbox
            checked={termsAccepted}
            onIonChange={(e) => onTermsChange(e.detail.checked)}
            aria-label="Saglasan sam sa uslovima kupovine"
            color={termsError ? "danger" : undefined}
            style={{ flexShrink: 0 }}
          />
          <IonText
            color={termsError ? "danger" : undefined}
            className="required"
            style={{ fontSize: 14, lineHeight: "20px" }}
          >
            Upoznat/a sam i saglasan/na sa{" "}
            <Link to="/uslovi" target="_blank">
              uslovima kupovine i plaćanja
            </Link>
            .
          </IonText>
        </div>
        {termsError && <ErrorText>{termsError}</ErrorText>}

        {method === "CARD" && (
          <div style={{ padding: "8px 10px 0" }}>
            <InfoMessage
              text={
                <>
                  Nakon potvrde porudžbine bićete preusmereni na zaštićenu stranicu
                  banke za unos podataka o kartici. Podaci o kartici nikada
                  nisu dostupni našem sistemu. Sva plaćanja se vrše u dinarima (RSD).
                  {currency === "KM" && (
                    <>
                      <br />
                      <b>
                        Kartica se zadužuje u dinarima:{" "}
                        <NumberMoney value={totalDin} forceCurrency="DIN" />
                      </b>
                      . Iznos u KM je informativan; Vaša banka ga preračunava po
                      svom kursu (videti izjavu o konverziji u uslovima kupovine).
                    </>
                  )}
                  {hasReservations && (
                    <>
                      <br />
                      Rezervisane slike se sada ne naplaćuju. Kartica se zadužuje
                      samo za proizvode koji su na stanju.
                    </>
                  )}
                  {config.isTest && (
                    <>
                      <br />
                      <b>TEST okruženje banke: koristite samo testne kartice.</b>
                    </>
                  )}
                </>
              }
            />
          </div>
        )}

        <div style={{ padding: "4px 10px" }}>
          <PaymentBrands />
        </div>
      </IonList>
    </IonCard>
  );
};
